import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { SquadDTO } from "@/types/squad.type";
import { useSquadActions } from "@/hooks/squads/use-squad-actions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { FormInput } from "@/components/campos-formulario/novos/FormInput";
import { FormTextarea } from "@/components/campos-formulario/novos/FormTextarea";

const editSquadSchema = z.object({
  nome: z.string().min(3, "O nome deve ter no mínimo 3 caracteres"),
  descricao: z.string().optional(),
});

type EditSquadFormData = z.infer<typeof editSquadSchema>;

interface SquadEditFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  squadToEdit: SquadDTO | null;
}

export function SquadEditFormDialog({ open, onOpenChange, squadToEdit }: SquadEditFormDialogProps) {
  // Assumindo que useSquadActions exporta a função updateSquad e o estado isUpdating
  const { updateSquad, isUpdating } = useSquadActions();

  const form = useForm<EditSquadFormData>({
    resolver: zodResolver(editSquadSchema),
    defaultValues: {
      nome: "",
      descricao: "",
    },
  });

  // O useEffect garante que o formulário seja preenchido sempre que o modal abrir
  // ou quando o squad selecionado na tabela mudar.
  useEffect(() => {
    if (squadToEdit && open) {
      form.reset({
        nome: squadToEdit.nome,
        descricao: squadToEdit.descricao || "",
      });
    }
  }, [squadToEdit, open, form]);

  const handleSubmit = async (data: EditSquadFormData) => {
    if (!squadToEdit) return;

    try {
      await updateSquad({
        id: squadToEdit.id,
        data: {
          nome: data.nome,
          descricao: data.descricao,
        },
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao atualizar squad:", error);
    }
  };

  // O onOpenChange local limpa o formulário caso o usuário feche no "X" ou clique fora
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) form.reset();
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editar Squad</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <div className="space-y-4">
              <Controller
                control={form.control}
                name="nome"
                render={({ field }) => (
                  <FormInput {...field} label="Nome do Squad" placeholder="Ex: Squad Alpha" />
                )}
              />

              <Controller
                control={form.control}
                name="descricao"
                render={({ field }) => (
                  <FormTextarea
                    {...field}
                    label="Descrição"
                    placeholder="Descrição curta (opcional)"
                    className="custom-scrollbar h-24 resize-none"
                  />
                )}
              />
            </div>

            <DialogFooter className="border-t border-border pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isUpdating}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isUpdating || !form.formState.isDirty} // Só habilita se houver alteração
              >
                {isUpdating ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
